import React from "react";
import { format, parseISO } from "date-fns";
import { Check, Circle, X } from "lucide-react";
import { buildOrderStatusSteps, humanizeDelta, colorForStep } from "../../Lib/orderStatusSteps";

const StepCircle = ({ state, color }) => {
  if (state === "current") {
    return (
      <span className='relative flex h-8 w-8 items-center justify-center shrink-0'>
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${color}`} />
        <span className={`relative inline-flex h-8 w-8 items-center justify-center rounded-full text-white ${color}`}>
          <span className='h-2.5 w-2.5 rounded-full bg-white animate-pulse' />
        </span>
      </span>
    );
  }
  if (state === "reached") {
    return (
      <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center border-2 border-transparent text-white ${color}`}>
        <Check className='h-4 w-4' />
      </div>
    );
  }
  if (state === "terminal") {
    return (
      <div className='w-8 h-8 shrink-0 rounded-full flex items-center justify-center border-2 bg-[#F4B7B5] text-[#590C07] border-[#F4B7B5]'>
        <X className='h-4 w-4' />
      </div>
    );
  }
  return (
    <div className='w-8 h-8 shrink-0 rounded-full flex items-center justify-center border-2 bg-white border-gray-300 text-gray-300'>
      <Circle className='h-3 w-3 fill-current' />
    </div>
  );
};

const formatTime = (time) => (time ? format(parseISO(time), "p, MMM d") : "");

/**
 * Full status timeline for the Track Order modal — labels, timestamps, and
 * per-step time deltas, built from the same step computation the table's
 * compact OrderProgressTrack uses.
 */
const RiderStatusTimeline = ({ statuses, currentStatus }) => {
  const { isTerminalAlt, steps, totalDeliveryMinutes } = buildOrderStatusSteps(
    statuses,
    currentStatus,
  );

  if (isTerminalAlt) {
    if (steps.length === 0) return null;
    return (
      <div className='w-full overflow-x-auto'>
        <div className='flex items-start min-w-max'>
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;
            return (
            <div
              key={step.id ?? step.name}
              className='flex-1 min-w-[100px] text-center relative animate-in fade-in zoom-in-95 duration-300'
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className='flex items-center justify-center'>
                <StepCircle
                  state={isLast ? "terminal" : "reached"}
                  color={colorForStep(step.name)}
                />
              </div>
              {!isLast && (
                <div
                  className={`absolute top-4 left-[calc(50%+16px)] w-full h-0.5 transition-all duration-500 ${
                    index + 1 === steps.length - 1 ? "bg-[#590C07]" : colorForStep(steps[index + 1].name)
                  }`}
                />
              )}
              <div className='mt-2 flex flex-col'>
                <h4 className='text-xs md:text-sm font-medium capitalize'>
                  {step.name.replace(/_/g, " ")}
                </h4>
                <p className='text-xs text-gray-500'>{formatTime(step.createdAt)}</p>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className='w-full'>
      <div className='overflow-x-auto'>
        <div className='flex items-start min-w-max'>
          {steps.map((step, index) => (
            <div
              key={step.name}
              className='flex-1 min-w-[110px] text-center relative animate-in fade-in zoom-in-95 duration-300'
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className='flex items-center justify-center'>
                <StepCircle
                  state={step.isCurrent ? "current" : step.reached ? "reached" : "upcoming"}
                  color={colorForStep(step.name)}
                />
              </div>
              {index !== steps.length - 1 && (
                <>
                  <div
                    className={`absolute top-4 left-[calc(50%+16px)] w-full h-0.5 transition-all duration-500 ${
                      step.reached && steps[index + 1].reached
                        ? colorForStep(steps[index + 1].name)
                        : "bg-gray-200"
                    }`}
                  />
                  {steps[index + 1].deltaSeconds != null && (
                    <p className='absolute top-6 left-[calc(50%+16px)] w-full text-[10px] text-gray-400'>
                      {humanizeDelta(steps[index + 1].deltaSeconds)}
                    </p>
                  )}
                </>
              )}
              <div className='mt-2 flex flex-col'>
                <h4
                  className={`text-xs md:text-sm font-medium capitalize ${
                    step.reached ? "text-primaryBlack" : "text-gray-400"
                  }`}
                >
                  {step.name.replace(/_/g, " ")}
                </h4>
                <p className='text-xs text-gray-500'>{formatTime(step.time)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {totalDeliveryMinutes != null && (
        <p className='text-xs text-gray-600 font-semibold mt-4 pt-3 border-t border-gray-100'>
          Total delivery time: {totalDeliveryMinutes} min
        </p>
      )}
    </div>
  );
};

export default RiderStatusTimeline;
